/**
 * QC Violations — Link Backfill sub-tab.
 *
 * Moved from Admin Maintenance into the QC hub since it is QC-specific.
 * Calls the new canonical `/qc-violations/backfill/*` endpoints. The
 * deprecated `/admin/qc-violation-link-diagnostic*` endpoints remain live
 * for one release and are NOT called by this component.
 *
 * Access is gated by the QC Access Control settings (backend-enforced).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  CheckCircle2,
  Database,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Stat = ({ label, value, highlight, warn, good }) => (
  <div
    className={
      'px-3 py-2 rounded-md border text-sm ' +
      (warn
        ? 'bg-amber-50 border-amber-200 text-amber-900'
        : good
        ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
        : highlight
        ? 'bg-blue-50 border-blue-200 text-blue-900'
        : 'bg-slate-50 border-slate-200 text-slate-700')
    }
  >
    <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-lg font-semibold">{String(value)}</div>
  </div>
);

const QCLinkBackfillTab = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const fetchDryRun = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/qc-violations/backfill/dry-run`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDryRun();
  }, [fetchDryRun]);

  const apply = async () => {
    setApplying(true);
    try {
      const r = await axios.post(
        `${API}/qc-violations/backfill/apply?confirm=APPLY-LINKS`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const body = r.data || {};
      // Defensive read — the older backend build could reply with an error
      // dict (no `stats` block) even on HTTP 200. Newer builds always send
      // stats. Cover both.
      const stats = body.stats;
      const written = Number(stats?.written) || 0;
      const applied = body.applied === true;

      if (stats) {
        // Happy path: backend returned the full report.
        if (applied) {
          if (written > 0) {
            toast.success(`Backfill applied — wrote ${written} record(s).`);
          } else if ((stats.would_link || 0) === 0 && (stats.already_linked_but_stale || 0) === 0) {
            toast.success('Backfill applied — nothing to write. Everything was already linked.');
          } else {
            toast.success('Backfill applied — no rows were written (all resolved as no-ops).');
          }
        } else {
          // Report present but not marked applied — treat as diagnostic-only response.
          toast.message('Backfill returned a diagnostic report (no writes attempted).');
        }
        setData(body);
      } else if (body.error) {
        // Backend short-circuited (e.g. missing confirm). Surface the hint.
        toast.error(
          body.hint
            ? `${body.error}: ${body.hint}`
            : `Backfill did not run: ${body.error}`,
        );
      } else {
        toast.success('Backfill apply request accepted.');
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg =
        (typeof d === 'string' ? d : d?.error) ||
        e.response?.data?.error ||
        e.message ||
        'Apply failed';
      toast.error(`Backfill failed: ${msg}`);
    } finally {
      setApplying(false);
    }
  };

  const s = data?.stats;
  const canApply = !!s && (s.would_link > 0 || s.already_linked_but_stale > 0);

  return (
    <Card data-testid="qc-backfill-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Database className="h-5 w-5 text-blue-600" />
          QC Violation Link Backfill
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-600 mb-3 max-w-3xl">
          Backfills the canonical <code>employee_id</code>, <code>employee_number</code>,
          and <code>employee_id_original</code> fields on QC violation records so
          they attach to the right employee. Dry-run first; Apply writes and is
          idempotent. The backfill preserves whatever ops originally sent.
        </p>
        {loading ? (
          <div className="p-6 text-center text-slate-400" data-testid="qc-backfill-loading">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : !data ? (
          <div className="text-sm text-slate-500">No data.</div>
        ) : !s ? (
          <div
            className="text-sm p-3 rounded bg-amber-50 text-amber-800 border border-amber-200"
            data-testid="qc-backfill-no-stats"
          >
            The backend returned no stats block
            {data.error ? ` (${data.error})` : ''}. Try &quot;Refresh&quot; to run a fresh dry-run.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Stat label="Total scanned" value={s.total_scanned} />
              <Stat label="Already OK" value={s.already_ok} />
              <Stat label="Would link" value={s.would_link} highlight={s.would_link > 0} />
              <Stat label="Stale link" value={s.already_linked_but_stale} highlight={s.already_linked_but_stale > 0} />
              <Stat label="Unresolvable" value={s.unresolvable} warn={s.unresolvable > 0} />
              <Stat label="Written" value={s.written} good={s.written > 0} />
              <Stat label="Read-only" value={data.read_only ? 'yes' : 'no'} />
              <Stat label="Applied" value={data.applied ? 'yes' : 'no'} good={data.applied} />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={fetchDryRun}
                disabled={loading}
                data-testid="qc-backfill-refresh"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh dry-run
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    disabled={!canApply || applying}
                    data-testid="qc-backfill-apply"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {applying
                      ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                    Apply Backfill
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="qc-backfill-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Apply the QC violation backfill?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will rewrite <strong>{s?.would_link ?? 0}</strong> new links
                      and correct <strong>{s?.already_linked_but_stale ?? 0}</strong>{' '}
                      stale link(s) on the qc_violations collection. Every change is
                      written to the audit log. The operation is idempotent —
                      re-running it after a successful apply is safe. Preserves what
                      ops originally sent on <code>employee_id_original</code>.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={apply} data-testid="qc-backfill-confirm-apply">
                      Yes, apply
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default QCLinkBackfillTab;
